import { useEffect, useRef, useState } from 'react';
import {
  motion,
  useTransform,
  useScroll,
  useSpring,
} from 'framer-motion';
import { cn } from '@/lib/utils';

interface TracingBeamProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * R2 - Tracing Beam effect that follows scroll progress
 * R4 - Adapted for full-width layouts with INBOCA brand colors
 * 
 * The beam runs along the left side of the page, connecting all sections visually.
 * Content inside maintains its original layout (no width constraints).
 */
export function TracingBeam({ children, className }: TracingBeamProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const [svgHeight, setSvgHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (contentRef.current) {
        setSvgHeight(contentRef.current.offsetHeight);
      }
    };

    updateHeight();
    
    // R4 - Recalculate on resize for responsive layouts
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const y1 = useSpring(
    useTransform(scrollYProgress, [0, 0.8], [50, svgHeight]),
    { stiffness: 500, damping: 90 }
  );
  
  const y2 = useSpring(
    useTransform(scrollYProgress, [0, 1], [50, svgHeight - 200]),
    { stiffness: 500, damping: 90 }
  );

  return (
    <motion.div
      ref={ref}
      className={cn('relative w-full', className)}
    >
      {/* Beam Container - Only visible on 2xl+ screens (≥1536px) where there's enough margin */}
      {/* z-[1] keeps beam above background-beams (z-0) but below content (z-10) */}
      <div className="absolute top-0 left-6 2xl:left-10 z-[1] hidden 2xl:block">
        {/* Starting dot indicator */}
        <motion.div
          transition={{ duration: 0.2, delay: 0.5 }}
          animate={{
            boxShadow:
              scrollYProgress.get() > 0
                ? 'none'
                : 'rgba(0, 151, 167, 0.4) 0px 3px 12px',
          }}
          className="ml-[27px] flex h-4 w-4 items-center justify-center rounded-full border border-inboca-teal/30 bg-white shadow-sm"
        >
          <motion.div
            transition={{ duration: 0.2, delay: 0.5 }}
            animate={{
              backgroundColor: scrollYProgress.get() > 0 ? 'white' : '#0097a7',
              borderColor: scrollYProgress.get() > 0 ? 'white' : '#0097a7',
            }}
            className="h-2 w-2 rounded-full border border-inboca-teal/50 bg-inboca-teal"
          />
        </motion.div>

        {/* SVG Beam Path */}
        <svg
          viewBox={`0 0 20 ${svgHeight}`}
          width="20"
          height={svgHeight}
          className="ml-4 block"
          aria-hidden="true"
        >
          {/* Background path (subtle guide line) */}
          <motion.path
            d={`M 1 0V -36 l 18 24 V ${svgHeight * 0.8} l -18 24V ${svgHeight}`}
            fill="none"
            stroke="#0097a7"
            strokeOpacity="0.12"
            transition={{ duration: 10 }}
          />
          
          {/* Animated gradient path */}
          <motion.path
            d={`M 1 0V -36 l 18 24 V ${svgHeight * 0.8} l -18 24V ${svgHeight}`}
            fill="none"
            stroke="url(#inboca-gradient)"
            strokeWidth="1.5"
            className="motion-reduce:hidden"
            transition={{ duration: 10 }}
          />
          
          <defs>
            {/* INBOCA brand gradient: Teal → Orange → Teal */}
            <motion.linearGradient
              id="inboca-gradient"
              gradientUnits="userSpaceOnUse"
              x1="0"
              x2="0"
              y1={y1}
              y2={y2}
            >
              <stop stopColor="#0097a7" stopOpacity="0" />
              <stop stopColor="#0097a7" />
              <stop offset="0.5" stopColor="#f57c00" />
              <stop offset="1" stopColor="#0097a7" stopOpacity="0" />
            </motion.linearGradient>
          </defs>
        </svg>
      </div>

      {/* Content - Full width, beam sits in natural side margins */}
      <div ref={contentRef} className="w-full">
        {children}
      </div>
    </motion.div>
  );
}
